type Resolver = (entries: unknown[]) => Promise<string[]>;

const looksLikeFirestoreId = (value: string): boolean => /^[A-Za-z0-9]{20}$/.test(value.trim());

const labelFromSnapshotEntry = (entry: unknown): string => {
  if (typeof entry === 'string') return entry.trim();
  if (!entry || typeof entry !== 'object') return '';
  const value = entry as Record<string, unknown>;
  return String(
    value.displayLabel ??
      value.label ??
      value.name ??
      value.optionLabel ??
      value.menuIngredientLabel ??
      ''
  ).trim();
};

const compactStrings = (entries: unknown): string[] =>
  Array.isArray(entries)
    ? entries.map((entry) => String(entry ?? '').trim()).filter(Boolean)
    : [];

const legacyDisplayLookup = (
  item: unknown,
  idsKey: string,
  displayKey: string
): { byId: Map<string, string>; byIndex: string[] } => {
  const cust = (item as any)?.customizations;
  const ids = compactStrings(cust?.[idsKey]);
  const displayValues = compactStrings(cust?.[displayKey]);
  const byId = new Map<string, string>();

  displayValues.forEach((label, index) => {
    if (looksLikeFirestoreId(label)) return;
    const id = ids[index];
    if (id && !byId.has(id)) {
      byId.set(id, label);
    }
  });

  return { byId, byIndex: displayValues };
};

export const extractCustomizationSnapshotLabels = (item: unknown): string[] => {
  const snapshot = (item as any)?.customizationSnapshot;
  if (!snapshot || typeof snapshot !== 'object') {
    return [];
  }

  const labelsFrom = (
    key: string,
    lookup?: { byId: Map<string, string>; byIndex: string[] }
  ): string[] => {
    const entries: unknown[] = Array.isArray((snapshot as any)[key]) ? (snapshot as any)[key] : [];
    return entries
      .map((entry, index) => {
        let label = labelFromSnapshotEntry(entry);
        if (lookup && (!label || looksLikeFirestoreId(label))) {
          const optionId =
            entry && typeof entry === 'object'
              ? String((entry as any).optionId ?? (entry as any).id ?? '').trim()
              : '';
          const fallback = (optionId ? lookup.byId.get(optionId) : undefined) ?? lookup.byIndex[index];
          if (fallback && !looksLikeFirestoreId(fallback)) {
            label = fallback;
          }
        }
        return label;
      })
      .filter(Boolean);
  };

  const included = labelsFrom(
    'includedOptions',
    legacyDisplayLookup(item, 'included_options', 'included_options_display')
  );
  const paid = labelsFrom(
    'paidSupplements',
    legacyDisplayLookup(item, 'paid_supplements', 'paid_supplements_display')
  );
  const removed = labelsFrom('removedIngredients');
  const labels = [...included, ...paid];
  if (removed.length > 0) {
    labels.push(`Sans: ${removed.join(', ')}`);
  }
  return labels;
};

export const resolveLegacyCustomizationLabels = async (
  item: unknown,
  resolveEntries: Resolver
): Promise<string[]> => {
  const cust = (item as any)?.customizations;
  if (!cust || typeof cust !== 'object') {
    return [];
  }

  const labels: string[] = [];
  const includedList = compactStrings(
    Array.isArray(cust.included_options_display) && cust.included_options_display.length > 0
      ? cust.included_options_display
      : cust.included_options
  );
  if (includedList.length > 0) {
    labels.push(...await resolveEntries(includedList));
  }

  const paidDisplay = compactStrings(cust.paid_supplements_display);
  if (paidDisplay.length > 0) {
    labels.push(...paidDisplay);
  } else {
    const paidIds = compactStrings(cust.paid_supplements);
    if (paidIds.length > 0) {
      labels.push(...await resolveEntries(paidIds));
    }
  }

  const removedList = compactStrings(
    Array.isArray(cust.removable_ingredients_display) && cust.removable_ingredients_display.length > 0
      ? cust.removable_ingredients_display
      : Array.isArray(cust.burger_remove_ingredients_display) && cust.burger_remove_ingredients_display.length > 0
        ? cust.burger_remove_ingredients_display
        : Array.isArray(cust.removable_ingredients) && cust.removable_ingredients.length > 0
          ? cust.removable_ingredients
          : cust.burger_remove_ingredients
  );
  if (removedList.length > 0) {
    labels.push(`Sans: ${removedList.join(', ')}`);
  }

  return labels;
};

export const extractLegacyCustomizationLabelsSync = (item: unknown): string[] => {
  const cust = (item as any)?.customizations;
  if (!cust || typeof cust !== 'object') {
    return [];
  }

  const labels: string[] = [];
  labels.push(...compactStrings(cust.included_options_display));
  labels.push(...compactStrings(cust.paid_supplements_display));

  const removedList = compactStrings(
    cust.removable_ingredients_display ||
      cust.burger_remove_ingredients_display ||
      cust.removable_ingredients ||
      cust.burger_remove_ingredients
  );
  if (removedList.length > 0) {
    labels.push(`Sans: ${removedList.join(', ')}`);
  }

  return labels;
};
